import { Component, type ReactNode } from 'react';
import { AlertOctagon, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  err?: Error;
}

/** 单页错误降级 — 任意 page 抛错时显示并提供"重试"按钮(reset state + reload) */
export class ErrorBoundary extends Component<Props, State> {
  state: State = {};

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  componentDidCatch(err: Error, info: { componentStack?: string }): void {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('[error-boundary]', err, info.componentStack);
    }
  }

  reset = () => this.setState({ err: undefined });

  override render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
        <div className="rounded-full bg-down/15 p-4 text-down">
          <AlertOctagon className="h-8 w-8" />
        </div>
        <div className="max-w-lg">
          <div className="text-lg font-bold text-ink-0">页面渲染异常</div>
          <div className="mt-2 text-sm text-ink-2 num">
            {this.state.err.message || '未知错误'}
          </div>
          {import.meta.env.DEV && this.state.err.stack && (
            <pre className="mt-4 text-[11px] text-ink-3 num bg-bg-3 rounded-md p-3 text-left max-h-48 overflow-y-auto whitespace-pre-wrap">
              {this.state.err.stack}
            </pre>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={this.reset}>
            <RotateCcw className="h-4 w-4" />
            重试
          </Button>
          <Button variant="outline" onClick={() => location.reload()}>
            完全刷新
          </Button>
        </div>
      </div>
    );
  }
}
